



























































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Header from '@/components/Header.vue';
import { ArticleInteractionBus, emitArticleAutocompleteFocused, emitOpenArticle } from '@/common/eventBus';
import loading from '@/components/loading';
import * as PleasedApi from '@/common/api/api.pleased';
import { ArticleDetail, ArticleRate } from '@/common/api/models.pleased';
import ThemeButton from './ThemeButton.vue';
import ArticleFeedback from './ArticleFeedback.vue';
import RelatedArticles from './RelatedArticles.vue';
import ArticleAutocomplete from './ArticleAutocomplete.vue';
import LikeIcon from '@/components/icons/LikeIcon.vue';
import DislikeIcon from '@/components/icons/DislikeIcon.vue';

import FilledLikeIcon from './icons/FilledLikeIcon.vue';
import FilledDislikeIcon from './icons/FilledDislikeIcon.vue';

import { namespace } from 'vuex-class';
import { buildArticleOption, buildConnectConfig, canConnectToAgent, randomId } from '@/common/bot/lib';
import { ArticleAction, ArticleInteractionExternEvent, ConnectToAgent } from '@/common/bot/types';
import FaAngleRight from './icons/FaAngleRight.vue';
import MdInfo from './icons/MdInfo.vue';
import { Bridge, sendToParrent } from '@/common/bridge';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import { CHECKS } from '@/common/bot/flow-frameworks/ChatMessageChecks.static';
import { resolveArticleAction } from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { manualTriggerCheck, MatchedChatTrigger } from '@/common/bot/flow-frameworks/ChatTrigger.cf.interceptor';

const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');

@Component({
  components: {
    Header,
    ThemeButton,
    ArticleFeedback,
    ArticleAutocomplete,
    RelatedArticles,
    LikeIcon,
    DislikeIcon,
    FilledLikeIcon,
    FilledDislikeIcon,
    FaAngleRight,
    MdInfo,
  },
  directives: {
    loading,
  },
})
export default class Article extends Vue {
  @Prop({ default: '' }) readonly articleId!: string;
  @Prop({ default: '' }) readonly title!: string;
  @Prop({ default: '' }) readonly categoryName!: string;
  @Prop({ default: '' }) readonly body!: string;
  @Prop({ default: '' }) readonly sectionTitle!: string;
  @Prop(Boolean) readonly fetchArticle!: boolean;
  @Prop(Boolean) readonly isFromAutocomplete!: boolean;
  @Prop() readonly sourceMessageId!: string;

  @userModule.State('isLogin') isLogin!: boolean;
  @articleAutocompleteModule.State('widgetTitle') widgetTitle!: string;

  $refs!: { container: HTMLDivElement }

  loading = false;
  loadingArticleConfig = false;
  articleParentConnectConfig: ConnectToAgent | null = null;
  showRetry = false;
  fetchedArticle: ArticleDetail | null = null;
  articleRating: ArticleRate | null = null;
  articleCheck = {
    showWarning: false,
    warningMessage: '',
  }

  cleanUps: Function[] = [];

  get ratingOptions () {
    return [
      { value: ArticleRate.HELPFUL, text: this.$t('page.article.feedback.helpful'), icon: 'LikeIcon', filledIcon: 'FilledLikeIcon' },
      { value: ArticleRate.NOT_HELPFUL, text: this.$t('page.article.feedback.not_helpful'), icon: 'DislikeIcon', filledIcon: 'FilledDislikeIcon' },
    ];
  }

  get articleBody () {
    return this.fetchedArticle?.body || this.body;
  }

  get articleTitle () {
    return this.fetchedArticle?.title || this.title;
  }

  get canShowAction () {
    const showWarning = !!this.articleCheck?.showWarning;
    if (!this.articleMetadata.articleAction) return { box: false, button: false, warning: false };
    if (this.articleMetadata.canConnectToSupport) return { box: true, button: true, warning: showWarning };
    return { box: true, button: false, warning: showWarning };
  }

  created () {
    window.history.pushState('forward', '', 'article'); // For handling Back button

    const backListener = () => {
      emitOpenArticle({
        isOpen: false,
      });
      window.removeEventListener('popstate', backListener);
      this.emitArticleInteraction('back');
    };
    window.addEventListener('popstate', backListener);
    this.cleanUps.push(() => window.removeEventListener('popstate', backListener));

    if (!this.fetchArticle) {
      this.fetchArticleParentConfig();
      return;
    }

    this.fetchArticleDetails();
    this.fetchArticleRating();
    this.fetchArticleParentConfig();
    this.checkArticleTrigger();
  }

  destroyed () {
    const container = document.getElementById('messageListContainer');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    this.cleanUps.map(x => x());
  }

  get articleMetadata () {
    const canConnectToSupport = !this.loading && this.articleParentConnectConfig && canConnectToAgent(this.articleParentConnectConfig);
    const articleAction = Boolean(this.fetchedArticle?.articleConfig.action) && resolveArticleAction({
      action: this.fetchedArticle!.articleConfig.action,
      actionMessage: this.fetchedArticle!.articleConfig.actionMessage,
      actionTitle: this.fetchedArticle!.articleConfig.actionText,
    } as ArticleAction);

    return {
      articleAction,
      canConnectToSupport,
    };
  }

  goToAction (action: ArticleAction) {
    Bridge.goToAction(action.action);
  }

  @Watch('articleId')
  onArticleIdChange () {
    this.fetchArticleDetails();
    this.fetchArticleRating();
    this.$refs.container.scrollTo(0, 0);
  }

  onRetryClick () {
    this.showRetry = false;
    this.fetchArticleDetails();
  }

  onArticleRate (rate: ArticleRate) {
    this.articleRating = rate;
    PleasedApi.rateArticle({
      articleId: +this.articleId,
      rating: rate,
    });
  }

  backCallback () {
    setTimeout(() => { emitArticleAutocompleteFocused(true); }, 100);
    history.back(); // To trigger popState listener
  }

  goToConnectSupport () {
    this.emitArticleInteraction('support-request');
    emitOpenArticle({ isOpen: false });
  }

  goToArticleAction () {
    if (!this.articleMetadata.articleAction) return;
    Bridge.goToAction(this.articleMetadata.articleAction.action);
  }

  articleInteracted = false;

  emitArticleInteraction (interaction: ArticleInteractionExternEvent['payload']['interaction']) {
    if (this.articleInteracted) return;
    this.articleInteracted = true;

    if (this.loadingArticleConfig || this.loading) {
      // TODO: wait for loading to complete
    }

    const article = this.fetchedArticle!;
    const category = this.fetchedArticle?.category;
    const section = this.fetchedArticle?.section;
    const showDefaultDescription = this.fetchedArticle?.articleConfig.showDescriptionBox !== true || !this.fetchedArticle?.articleConfig.descriptionMessage;
    const descriptionMessage = showDefaultDescription ? (this.$t('global.describe') as string) : this.fetchedArticle?.articleConfig.descriptionMessage!;

    const articleOption = buildArticleOption({ ...article.articleConfig, id: article.id, title: article.title, category: category?.titleTranslated || category?.title || '' });
    ArticleInteractionBus.pub({
      id: 'ArticleInteraction-' + randomId(),
      type: ExternalBotEvent.ARTICLE_PAGE_INTERACTION,
      payload: {
        interaction,
        article: {
          ...articleOption,
          ...(articleOption.action ? { action: resolveArticleAction(articleOption.action) } : {}),
        },
        supportOptions: {
          ...this.articleParentConnectConfig! || {},
          showDescriptionBox: this.fetchedArticle?.articleConfig.showDescriptionBox ?? true,
          descriptionMessage: descriptionMessage,
          transactionType: this.fetchedArticle?.articleConfig.transactionType || '',
          category: category?.titleTranslated || category?.title || '',
          section: section?.titleTranslated || section?.title || '',
        },
        sourceMessageId: this.sourceMessageId,
      },
    });
  }

  fetchArticleDetails () {
    this.loading = true;

    PleasedApi.getArticleDetails.cached({ articleId: this.articleId }).then((res) => {
      this.fetchedArticle = res.data;
    })
      .catch(() => {
        this.showRetry = true;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  fetchArticleRating () {
    PleasedApi.getArticleRating({ articleId: +this.articleId }).then((res) => {
      this.articleRating = res.data.rating;
    });
  }

  checkArticleTrigger () {
    manualTriggerCheck({ type: 'ARTICLE', id: this.articleId }, matched => {
      if (matched) this.showOverlayTrigger(matched);
    });
  }

  fetchArticleParentConfig () {
    if (this.loadingArticleConfig) return;
    this.loadingArticleConfig = true;

    PleasedApi.getArticleParentConfig.cached({ articleId: this.articleId })
      .then(sectionConfig => buildConnectConfig({ ...sectionConfig.data }))
      .then(config => {
        this.articleParentConnectConfig = config;
        this.runCheck();
      })
      .finally(() => {
        this.loadingArticleConfig = false;
      });
  }

  async runCheck () {
    if (!this.isLogin || !this.fetchedArticle?.articleConfig.checks) return;
    await CHECKS.KYC.check().then(checkPassed => {
      if (checkPassed) {
        this.articleCheck = {
          showWarning: false,
          warningMessage: '',
        };
      } else {
        this.articleCheck = {
          showWarning: true,
          warningMessage: CHECKS.KYC.slots().warning,
        };
      }
    });
  }

  showOverlayTrigger (trigger: MatchedChatTrigger) {
    const triggerDialog = this.$dialog({
      content: trigger.message,
      className: 'overlay-trigger',
      buttons: [{
        name: this.$t('global.end_chat'),
        callback: async () => {
          await trigger.report.end();
          triggerDialog.close();
          sendToParrent('CLOSE');
        },
      }, {
        name: this.t('global.continue', 'Continue'),
        class: 'primary',
        callback: () => {
          trigger.report.continue();
          triggerDialog.close();
        },
      }],
    });
  }
}
